import { useState } from "react";

// Shared user avatar (#1038, picture branch added by #1056). The monogram is
// still what most users see — it is the default state for every local-password
// account with no upload and no Google picture — so it has to look deliberate
// rather than like a missing image.

// Only the tints that define BOTH a background and a text token in each theme,
// so the monogram stays legible in light and dark without a per-colour override.
const MONOGRAM_PALETTE = [
  { bg: "var(--tint-blue-bg)", ink: "var(--tint-blue-text)" },
  { bg: "var(--tint-green-bg)", ink: "var(--tint-green-text)" },
  { bg: "var(--tint-gold-bg)", ink: "var(--tint-gold-text)" },
  { bg: "var(--tint-coral-bg)", ink: "var(--tint-coral-text)" }
] as const;

/**
 * First character of the first two words, uppercased; one character for a
 * single-word name. Falls back to a neutral glyph rather than rendering an
 * empty circle, since a name is not guaranteed to contain a letter.
 *
 * Exported for unit testing — pure, so it fits the node-env web harness.
 */
export function monogramInitials(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  const letters = words.slice(0, 2).map((word) => [...word][0] ?? "").join("");
  return letters ? letters.toUpperCase() : "?";
}

/**
 * Deterministic colour choice, so a given member is always the same colour to
 * everyone in the household. Keyed on the user id rather than the name so a
 * rename does not reshuffle it.
 */
// `paletteSize` is annotated rather than inferred: MONOGRAM_PALETTE is `as
// const`, so its `.length` is the literal 4 and an inferred default would make
// this parameter accept only that one value.
export function monogramPaletteIndex(userId: string, paletteSize: number = MONOGRAM_PALETTE.length): number {
  let hash = 0;
  for (const char of userId) {
    hash = (hash * 31 + char.codePointAt(0)!) % 1_000_000_007;
  }
  return paletteSize > 0 ? hash % paletteSize : 0;
}

export type UserAvatarProps = {
  name: string;
  /** Keys the deterministic monogram colour. */
  userId: string;
  /** Versioned URL from the auth payload or members list (#1056). */
  avatarUrl?: string | null;
  size?: number;
};

export function UserAvatar({ name, userId, avatarUrl, size = 32 }: UserAvatarProps) {
  // Reset on url change so replacing a broken picture with a good one recovers
  // rather than staying stuck on the monogram for the rest of the session.
  const [failedUrl, setFailedUrl] = useState<string | null>(null);
  const showImage = Boolean(avatarUrl) && failedUrl !== avatarUrl;

  if (showImage && avatarUrl) {
    return (
      <img
        className="user-avatar"
        src={avatarUrl}
        // Empty alt: the wrapping control carries the label.
        alt=""
        width={size}
        height={size}
        // A file can go missing under a row that still claims one — a restored
        // database, a wiped upload directory, an expired session. Falling back
        // beats rendering a broken image.
        onError={() => setFailedUrl(avatarUrl)}
      />
    );
  }

  const tint = MONOGRAM_PALETTE[monogramPaletteIndex(userId)] ?? MONOGRAM_PALETTE[0];
  return (
    <span
      className="user-avatar"
      // Equivalent of an empty alt: the control wrapping this carries the
      // label, so announcing the initials again would just be noise.
      aria-hidden="true"
      style={{
        width: size,
        height: size,
        background: tint.bg,
        color: tint.ink,
        fontSize: Math.max(10, Math.round(size * 0.4))
      }}
    >
      {monogramInitials(name)}
    </span>
  );
}
